import { List, Map } from 'immutable';
import moment from 'moment';
import momentTZ from 'moment-timezone';
import qs from 'query-string';
import r from 'render-dom';
import { isURL } from 'validator';

import { addAlert } from 'components/shared/Alert';
import Spinner from 'components/shared/Spinner';
import {
    ARCHIVE,
    END,
    FEATURE,
    PAUSE,
    START,
    STATE,
    STATUS,
} from 'constants/campaigns';
import {
    archiveCampaign,
    endCampaign,
    pauseCampaign,
    startCampaign,
} from 'redux/campaign/actions';

export const parseQueryString = (query) => {
    return qs.parse(query);
};

export const updateQueryString = (history, newQuery) => {
    const currentQuery = parseQueryString(window.location.search);

    history.push(`${window.location.pathname}?${qs.stringify({ ...currentQuery, ...newQuery })}`);
};

export const bool = (expression, truthy, falsey) => {
    return expression ? truthy : falsey;
};

export const componentOr = bool;

export const componentOrNull = (expression, truthy) => bool(expression, truthy, null);

export const componentOrSpinner = (expression, truthy) => bool(expression, truthy, r(Spinner));

// updates pivot fields in lists to reflect current sort order
export const updateSortOrder = (list) => list.map(
    (item, index) => {
        return item.setIn(['pivot', 'sort'], index)
            .setIn(['pivot', 'primary'], Number(!index));
    }
);

export const getMediumImageUrl = (medium, slug, strategy, width, height) => {
    if (!medium) {
        return '';
    }

    if (medium.get('type') === 'YOUTUBE') {
        return `https://i1.ytimg.com/vi/${medium.getIn(['metadata', 'id'])}/hqdefault.jpg`;
    }
    const id = medium.get('id');
    const ext = medium.getIn(['metadata', 'extension'], 'jpg');

    let dimensions = width;

    if (height) {
        dimensions = `${dimensions}/${height}`;
    }

    return `//${process.env.REACT_APP_IMAGE_API_URL}/${strategy}/${dimensions}/${id}-${slug}.${ext}`;
};

export const getPrimaryImage = (media) => {
    if (media.size) {
        return media.filter((medium) => medium.getIn(['pivot', 'primary'], false)).first();
    }

    return null;
};

// This function will give you back a status if you pass a state or state if you pass a status
export const getCampaignStatus = (campaign) => {
    const campaignState = campaign.get('state');
    const isPending = campaign.getIn(['applications', 0, 'pivot', 'state']) === STATUS.PENDING;
    const isOver = moment().isAfter(moment(campaign.get('endsAt')));
    let published = STATUS.LIVE;

    if (isPending) {
        published = STATUS.PENDING;
    }
    if (isOver) {
        published = STATUS.ENDED;
    }
    const statuses = [
        [STATE.ARCHIVED, STATUS.ARCHIVED],
        [STATE.DRAFT, STATUS.DRAFT],
        [STATE.PAUSED, STATUS.PAUSED],
        [STATE.PUBLISHED, published],
        [STATE.STOPPED, STATUS.ENDED],
    ];

    const [state, status] = statuses.filter((tup) => {
        return tup.includes(campaignState);
    }).pop();

    return state === campaignState ? status : state;
};

export const getCampaignActions = (status) => {
    const archive = {
        action: archiveCampaign,
        name: ARCHIVE,
    };
    const end = {
        action: endCampaign,
        name: END,
    };
    const feature = {
        action: Function.prototype,
        name: FEATURE,
    };
    const pause = {
        action: pauseCampaign,
        name: PAUSE,
    };
    const start = {
        action: startCampaign,
        name: START,
    };

    switch (status) {
        case STATUS.PAUSED:
            return [start, end, feature];

        case STATUS.ARCHIVED:
            return [];

        case STATUS.ENDED:
            return [archive];

        case STATUS.DRAFT:
            return [end, feature];

        default:
            return [pause, end, feature];
    }
};

export const getStatusActions = (campaign) => {
    const status = getCampaignStatus(campaign);

    return {
        actions: getCampaignActions(status),
        status,
    };
};

export const formatDateToPST = (dateString) => {
    if (dateString) {
        return momentTZ.tz(dateString, 'America/Los_Angeles').format('M/D/Y');
    }

    return '--';
};

export const formatPriceIfNeeded = (val) => {
    if (val && val % 1 !== 0) {
        return val.toFixed(2);
    }

    return val;
};

export const formatThousands = (num) => {
    if (num >= 1000) {
        const numInThousands = num / 1000;
        const digitAfterDecimal = Math.trunc(numInThousands * 10) % 10;
        const decimal = digitAfterDecimal ? `.${digitAfterDecimal}` : '';

        return `${Math.trunc(numInThousands)}${decimal}k`;
    }

    return num;
};

export const scrollToTop = () => window.scrollTo(0, 0);

export const isNumber = (n) => !Number.isNaN(parseFloat(n)) && Number.isFinite(n);

export const renderInfinity = (n) => (n === -1 ? '∞' : n);

export const targetBlank = (href) => ({ href, rel: 'noopener noreferrer', target: '_blank' });

export function createSymbols(constants) {
    const symbols = {};

    constants.forEach((constant) => {
        symbols[constant] = Symbol(constant);
    });

    return symbols;
}

export function createCollectionSymbols(name) {
    return {
        // TODO: Remove these "DELETE" constants (should be on the resource not the collection)
        DELETE: `DELETE_${name}`,
        DELETE_FAIL: `DELETE_${name}_FAIL`,
        DELETE_SUCCESS: `DELETE_${name}_SUCCESS`,
        EDIT_IN: `EDIT_IN_${name}_COLLECTION`,
        FETCH: `FETCH_${name}_COLLECTION`,
        FETCH_FAIL: `FETCH_${name}_COLLECTION_FAIL`,
        FETCH_SUCCESS: `FETCH_${name}_COLLECTION_SUCCESS`,
        POST: `POST_${name}`,
        POST_FAIL: `POST_${name}_FAIL`,
        POST_SUCCESS: `POST_${name}_SUCCESS`,
        RESET: `RESET_${name}`,
    };
}

export function createResourceSymbols(name) {
    return {
        DELETE: `DELETE_${name}`,
        DELETE_FAIL: `DELETE_${name}_FAIL`,
        DELETE_SUCCESS: `DELETE_${name}_SUCCESS`,
        EDIT_IN: `EDIT_IN_${name}`,
        FETCH: `FETCH_${name}`,
        FETCH_FAIL: `FETCH_${name}_FAIL`,
        FETCH_SUCCESS: `FETCH_${name}_SUCCESS`,
        PATCH: `PATCH_${name}`,
        PATCH_FAIL: `PATCH_${name}_FAIL`,
        PATCH_SUCCESS: `PATCH_${name}_SUCCESS`,
        PUT: `PUT_${name}`,
        PUT_FAIL: `PUT_${name}_FAIL`,
        PUT_SUCCESS: `PUT_${name}_SUCCESS`,
        RESET: `RESET_${name}`,
    };
}

export const onCardSubmit = (cb) => (values) => {
    Stripe.setPublishableKey(process.env.REACT_APP_STRIPE_KEY);
    const cvc = values.get('cvc', '');
    const expiry = values.get('expiry', '');
    const name = values.get('name', '');
    const number = values.get('number', '');
    const { card } = Stripe;
    const [month, year] = expiry.split('/');

    if (!card.validateCardNumber(number)) {
        addAlert({ message: 'There was an error with your card number' });
    } else if (!card.validateExpiry(month, year)) {
        addAlert({ message: 'There was an error with your card expiration date' });
    } else if (!card.validateCVC(cvc)) {
        addAlert({ message: 'There was an error with your card cvc' });
    } else {
        card.createToken({
            cvc,
            exp_month: month,
            exp_year: year,
            name,
            number,
        }, (status, response) => {
            if (status === 200) {
                addAlert({ message: 'Your credit card was updated' });

                return cb(response);
            }

            addAlert({ message: 'There was an error with your card!' });

            return null;
        });
    }
};

export const handleFormProps = ({ nextProps }) => {
    if (nextProps.submitSucceeded) {
        nextProps.reset();
    }
};

export const getPlanFeatures = (plan) => {
    const convertValue = (value) => value > -1 ? value : Infinity;
    const checkUnlimited = (val) => Number.isFinite(val) ? val : 'Unlimited';
    const boost = convertValue(plan.get('boosts'));
    const campaigns = convertValue(plan.get('campaigns'));
    const videos = convertValue(plan.get('videos'));
    const keywords = convertValue(plan.get('keywords'));

    return [
        `${checkUnlimited(campaigns)} campaign${campaigns > 1 ? 's' : ''}`,
        `${checkUnlimited(videos)} video${videos !== 1 ? 's' : ''} per month`,
        `${checkUnlimited(keywords)} products tracked`,
        boost ? `${checkUnlimited(boost)} keyword boost${boost !== 1 ? 's' : ''}` : '',
        plan.get('followups') ? 'Email shoppers' : '',
        'Unlimited landing pages',
        plan.get('chat') ? 'Live chat with support' : '',
    ];
};

export const trackCampaignValues = ({ id, name, state, type }) => ({ id, name, state, type });

export const addOrdinalSuffix = (n) => {
    // eslint-disable-next-line
    const suffix = ['', 'st', 'nd', 'rd'][n % 100 >> 3 ^ 1 && n % 10] || 'th';

    return `${n}${suffix}`;
};

export const getAsinFromURL = (url) => {
    if (isURL(url)) {
        const match = url.match(/amazon\.[a-z.]+\/([\w-]+\/)?(dp|gp\/product)\/(\w+\/)?(\w{10})/);

        return match ? match[4] : null;
    }

    return null;
};

export const getAsinFromSearch = (search) => {
    const asin = getAsinFromURL(search);

    if (asin) {
        return asin;
    }

    if (search.length === 10) {
        return search;
    }

    return null;
};

export const getBoostFields = (boost) => {
    const fields = boost.get('fields', Map({})) || Map({});

    return {
        brand: fields.get('brand', '') || '',
        ean: fields.get('ean', '') || '',
        merchant: fields.get('merchant', '') || '',
        queries: boost.get('queries', List([''])) || List(['']),
        tos: fields.get('tos', false),
    };
};

export const getBoostUrlFromQuery = ({ asin, boost, query }) => {
    const { brand, ean, merchant } = getBoostFields(boost);
    const keywordString = `?field-keywords=${encodeURIComponent(query).replace(/%20/g, '+')}`;
    const brandString = brand ? `&field-brand=${encodeURIComponent(brand).replace(/%20/g, '+')}` : '';
    const eanString = ean ? `&field-ean=${ean}` : '';
    const asinString = asin ? `&field-asin=${asin}` : '';
    const merchantString = merchant ? `&merchant=${merchant}` : '';
    const fields = [
        keywordString,
        brandString,
        eanString,
        asinString,
        merchantString,
    ].join('');
    const url = `https://www.amazon.com/s${fields}`;

    return url.split(' ').join('+');
};

/* eslint-disable */
export const hideChat = () => window.zE && window.zE('webWidget', 'hide');
export const showChat = () => window.zE && window.zE('webWidget', 'show');
/* eslint-enable */

export const getReportDefaultFilter = () => {
    const lastMonth = moment()
        .startOf('month')
        .subtract(1, 'days');

    return qs.stringify({ month: lastMonth.format('M'), year: lastMonth.get('year') });
};

export const openInNewTab = (url) => {
    const a = document.createElement('a');
    a.target = '_blank';
    a.href = url;
    a.click();
};



// WEBPACK FOOTER //
// ./src/utils/helpers.js